import type { Vec2, Vec3 } from '../../geo/types';
import { hexToLinear, type LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { MeshBuilder, VertexStyle, Frame } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import type { Lot, HouseParams } from './types';
import { roofSlopeLength } from './gables';
import { orientedFace } from './solids';
import { lotFrame } from './lotFrame';
import type { Body, FacadeStyles } from './houseFacades';
import type { PartyWalls } from './partyWalls';
import type { HouseStreets } from './houseStreets';
import type { ShopFronts } from './shops';
import type { ShopWares } from './shopWares';
import { JOINERY } from './openingDetail';
import type { StoepPlan } from './stoep';
import { LUIFEL } from './luifel';
import { PATTERN } from '../../render/patterns/patternIds';
import type { ShopRoomColors, ShopRoomStyles } from './shopRoom';

export interface HouseGeometry {
  readonly eavesHeight: number;
  readonly ridgeHeight: number;
  readonly triangleCount: number;
  readonly frontStep?: { readonly reach: number; readonly height: number };
}

export interface HouseColors {
  readonly wall: LinearRGB;
  readonly roof: LinearRGB;
  readonly trim: LinearRGB;
  readonly glass: LinearRGB;
  readonly leaf: LinearRGB;
  /** The stoepen's. */
  readonly stone: LinearRGB;
  /** A shop's front room, under the light its opening lets in (shopRoom.ts). */
  readonly room: ShopRoomColors;
  /** A shop's hung iron implements, black as the wall anchors. */
  readonly iron: LinearRGB;
}

/** Everything that decides one house, apart from where its triangles go. */
export interface HousePlan {
  readonly openFrontDoor?: boolean;
  readonly lot: Lot;
  readonly params: HouseParams;
  readonly colors: HouseColors;
  readonly edgeId: number;
  readonly seed: number;
  readonly party: PartyWalls;
  /** The streets its front and free side walls face (houseStreets.ts). */
  readonly streets: HouseStreets;
  /** Where its ground floor sells, drawn from its streets (shops.ts). */
  readonly shop: ShopFronts;
  /** Its stoepen, drawn from its streets and its shop (stoep.ts). */
  readonly stoep: StoepPlan;
  /** What its shop shows of its wares (shopWares.ts). */
  readonly wares: ShopWares;
  readonly rng: Rng;
}

export interface HouseStyles extends FacadeStyles {
  /**
   * The side walls: brick on every house since the ordinances of 1452-1524 (docs/plans/houses.md),
   * in the front's colour, as the painting gives side walls no colour of their own.
   */
  readonly side: VertexStyle;
  readonly roof: VertexStyle;
  readonly trim: VertexStyle;
  readonly stone: VertexStyle;
  readonly luifel: VertexStyle;
  readonly room: ShopRoomStyles;
  /** A shop's shutters and the struts under its awning, in the window row's red-brown. */
  readonly shutter: VertexStyle;
  readonly iron: VertexStyle;
}

const LUIFEL_WOOD = hexToLinear(LUIFEL.colour);
const SHUTTER_WOOD = hexToLinear(JOINERY.shutter);

/* Residential end walls are timber-clad under the current reconstruction policy. The existing
   frontage parameter retains the flush/jettied silhouette without changing roofs or plots. */
const ROOF_PATTERN = { tile: PATTERN.roofTileHollePan, slate: PATTERN.slateMaas } as const;

export function houseFrame(lot: Lot): Frame {
  const { frontMid, facing, shear } = lotFrame(lot);
  return {
    origin: layoutToWorld(frontMid, 0),
    yaw: Math.atan2(-facing[1], -facing[0]),
    ...(shear === undefined ? {} : { shear }),
  };
}

/* A front shades as the painting class of its colour, whatever it is built of. */
export function houseStyles(
  plan: HousePlan,
  width: number,
  depth: number,
  eaves: number
): HouseStyles {
  const { params, colors } = plan;
  const base = { edgeId: plan.edgeId, seed: plan.seed, strokeAngle: 0 };
  const wall = {
    ...base,
    color: colors.wall,
    surface: SURFACE.wood,
  };
  return {
    wall: { ...wall, extent: [width, eaves], pattern: PATTERN.timberFront1538 },
    side: {
      ...wall,
      surface: SURFACE.brick,
      extent: [depth, eaves],
      pattern: PATTERN.houseBrick1538,
    },
    roof: {
      ...base,
      color: colors.roof,
      surface: params.roofMaterial === 'slate' ? SURFACE.roofSlate : SURFACE.roofTile,
      extent: [depth, roofSlopeLength(width, params.pitch)],
      pattern: ROOF_PATTERN[params.roofMaterial],
    },
    trim: { ...base, color: colors.trim, surface: SURFACE.wood, extent: [0, 0] },
    stone: { ...base, color: colors.stone, surface: SURFACE.stone, extent: [0, 0] },
    luifel: { ...base, color: LUIFEL_WOOD, surface: SURFACE.wood, extent: [0, 0] },
    shutter: { ...base, color: SHUTTER_WOOD, surface: SURFACE.wood, extent: [0, 0] },
    iron: { ...base, color: colors.iron, surface: SURFACE.stone, extent: [0, 0] },
    glass: { ...base, color: colors.glass, surface: SURFACE.linework, extent: [0, 0] },
    leaf: { ...base, color: colors.leaf, surface: SURFACE.wood, extent: [0, 0] },
    room: {
      floor: { ...base, color: colors.room.floor, surface: SURFACE.brick, extent: [0, 0] },
      wall: { ...base, color: colors.room.wall, surface: SURFACE.brick, extent: [0, 0] },
      wood: { ...base, color: colors.room.wood, surface: SURFACE.wood, extent: [0, 0] },
      ceiling: { ...base, color: colors.room.ceiling, surface: SURFACE.wood, extent: [0, 0] },
    },
  };
}

export function buildRoof(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  body: Body
): void {
  const { x0, depth, half, eaves, ridge } = body;
  for (const side of [1, -1] as const) {
    const z = side * half;
    const slope: Vec3[] = [
      [x0, eaves, z],
      [depth, eaves, z],
      [depth, ridge, 0],
      [x0, ridge, 0],
    ];
    /* Courses run rightward from the slope's left end seen from outside (STYLE §15.1): back on the +z slope, front on the other. */
    const uv = (p: Vec3): Vec2 => [
      side === 1 ? p[0] - x0 : depth - p[0],
      Math.hypot(half - Math.abs(p[2]), p[1] - eaves),
    ];
    orientedFace(builder, style, frame, slope, [0, 1, side], uv);
  }
}
